import React, { MouseEventHandler, useEffect, useRef, useState } from "react";

import PlusIcon from "@uui/PlusCircle";
import { isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import TableCell from "@app/analytics/components/Table/TableCell";
import Tag, { CustomTag } from "@app/analytics/components/Tag";
import { TagsContainer } from "@app/analytics/MeetingReports/ReportsTable/MeetingReportTableRow/styles";
import useMeasure from "@app/common/hooks/useMeasure";
import type { Tag as ITag } from "@app/common/types";

import { Row } from "@rui/Stack";

const EMPTY_ELEMENTS: HTMLDivElement[] = [];

const CELL_WIDTH_PX = 200;
const MAX_TAG_WIDTH = "184px"; // cell width - 16px padding
const ADD_TAGS_BUTTON_WIDTH = 42;
const TAG_ACTIONS_CONTAINER_ID = "tag-actions";
const TAG_HEIGHT = 20; // in px
const GAP = 2; // in px

const MeetingReportTagsCell = ({
  tags = [],
  onClick,
  asCell = true,
  rows = 1,
}: {
  tags?: ITag[];
  onClick?: MouseEventHandler<HTMLDivElement>;
  asCell?: boolean;
  rows?: number;
}) => {
  const [numHiddenTags, setNumHiddenTags] = useState(0);
  const tagsContainerRef = useRef<HTMLDivElement | null>(null);

  const [hiddenCountTagRef, , { innerWidth }] = useMeasure();

  const canEditTags = Boolean(onClick);

  useEffect(() => {
    const tagElements = tagsContainerRef.current?.children;
    const elements = tagElements
      ? (Array.from(tagElements) as HTMLDivElement[])
      : EMPTY_ELEMENTS;

    if (elements.length > 0) {
      const containerBottom = tagsContainerRef.current
        ? tagsContainerRef.current.offsetTop +
          tagsContainerRef.current.offsetHeight
        : -1;

      let hiddenTagsCount = 0;
      let remainingWidth =
        CELL_WIDTH_PX - (canEditTags ? ADD_TAGS_BUTTON_WIDTH : 0);

      if (containerBottom > 0 && tagElements != null) {
        elements.forEach((element) => {
          if (element.id === TAG_ACTIONS_CONTAINER_ID) return;

          const isHidden =
            remainingWidth <= 0 || element.offsetTop >= containerBottom;
          const elementBottom = element.offsetTop + element.offsetHeight;
          if (isHidden) {
            hiddenTagsCount += 1;
            element.style.display = "none";
          } else if (elementBottom === containerBottom) {
            remainingWidth -= element.offsetWidth;
            if (remainingWidth <= 0) {
              element.style.display = "none";
              hiddenTagsCount += 1;
            }
          }
        });
      }
      if (hiddenTagsCount != numHiddenTags) {
        setNumHiddenTags(hiddenTagsCount);
      }
    }
    return () => {
      elements.forEach((el) => (el.style.display = "flex"));
    };
  }, [tags, innerWidth]);

  const handleClickContainer: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    onClick?.(e);
  };
  const showAddTagsLabelByDefault = isEmpty(tags);

  const maxHeight = rows * TAG_HEIGHT + GAP * (rows - 1);

  const content = (
    <TagsContainer
      showLabel={showAddTagsLabelByDefault}
      onClick={handleClickContainer}
    >
      <Row
        spacing="2px"
        flexWrap={rows > 1 ? "wrap" : undefined}
        maxHeight={`${maxHeight}px`}
        overflow="hidden"
        ref={tagsContainerRef}
      >
        {tags.map((tag) => (
          <Tag key={tag.id} tag={tag} sx={{ maxWidth: MAX_TAG_WIDTH }} />
        ))}
        <Row id={TAG_ACTIONS_CONTAINER_ID} variant="left" spacing="2px">
          {numHiddenTags > 0 && (
            <CustomTag
              backgroundColor="shades.neutral.100"
              color="shades.neutral.500"
              ref={hiddenCountTagRef}
            >
              +{numHiddenTags}
            </CustomTag>
          )}
          {canEditTags && (
            <Row
              variant="left"
              spacing={0.25}
              className="add-tag"
              width="fit-content"
            >
              <PlusIcon sx={{ fontSize: "12px" }} />
              <Typography variant="labelReg" className="add-tag__label">
                Tag
              </Typography>
            </Row>
          )}
        </Row>
      </Row>
    </TagsContainer>
  );

  if (asCell) {
    return (
      <TableCell
        sx={{ width: `${CELL_WIDTH_PX}px`, minWidth: `${CELL_WIDTH_PX}px` }}
      >
        {content}
      </TableCell>
    );
  }

  return content;
};

export default MeetingReportTagsCell;
